import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ClipboardList, Eye, Mail, Pencil, Plus } from 'lucide-react';
import { toast } from 'sonner';

import api from '../services/api';
import { previewDocumentPdf } from '../lib/documentPreview';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Textarea } from './ui/textarea';


function clientLocation(client = {}) {
  return [client.address, client.city, client.province, client.postal_code].filter(Boolean).join(', ');
}


function localDateTimeInput(value = null) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 16);
}


function apiDateTime(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}


function formatSchedule(value) {
  if (!value) return 'Not scheduled';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}


function emptyForm(project) {
  const client = project?.client || {};
  return {
    recipient_type: 'employee',
    recipient_user_id: 'manual',
    recipient_name: '',
    recipient_email: '',
    scheduled_start: '',
    scheduled_end: '',
    customer_name: client.name || '',
    customer_contact_name: client.primary_contact_name || client.contact_person || '',
    customer_phone: client.phone || '',
    service_location: clientLocation(client),
    job_scope: project?.description || '',
    tools_materials: '',
    access_instructions: '',
    safety_notes: '',
    parking_instructions: '',
    internal_notes: '',
  };
}


function noteForm(note) {
  return {
    recipient_type: note.recipient_type || 'employee',
    recipient_user_id: note.recipient_user_id || 'manual',
    recipient_name: note.recipient_name || '',
    recipient_email: note.recipient_email || '',
    scheduled_start: localDateTimeInput(note.scheduled_start),
    scheduled_end: localDateTimeInput(note.scheduled_end),
    customer_name: note.customer_name || '',
    customer_contact_name: note.customer_contact_name || '',
    customer_phone: note.customer_phone || '',
    service_location: note.service_location || '',
    job_scope: note.job_scope || '',
    tools_materials: note.tools_materials || '',
    access_instructions: note.access_instructions || '',
    safety_notes: note.safety_notes || '',
    parking_instructions: note.parking_instructions || '',
    internal_notes: note.internal_notes || '',
  };
}


function statusClass(status) {
  if (status === 'sent') return 'bg-emerald-100 text-emerald-800';
  if (status === 'failed') return 'bg-red-100 text-red-800';
  return 'bg-slate-100 text-slate-700';
}


function responseClass(status) {
  if (status === 'accepted') return 'bg-emerald-100 text-emerald-800';
  if (status === 'declined') return 'bg-red-100 text-red-800';
  if (status === 'alternative_proposed') return 'bg-violet-100 text-violet-800';
  if (status === 'pending') return 'bg-amber-100 text-amber-800';
  return 'bg-slate-100 text-slate-600';
}


export function ServiceNotesPanel({ project }) {
  const [notes, setNotes] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(() => emptyForm(project));
  const [saving, setSaving] = useState(false);
  const [sendingId, setSendingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [noteResponse, recipientResponse] = await Promise.all([
        api.get(`/service-notes?project_id=${project.id}`),
        api.get('/service-notes/recipients'),
      ]);
      setNotes(noteResponse.data || []);
      setRecipients(recipientResponse.data || []);
    } catch (error) {
      toast.error('Unable to load service notes. / Einsatzaufträge konnten nicht geladen werden.');
    }
  }, [project.id]);

  useEffect(() => { load(); }, [load]);

  const recipientById = useMemo(
    () => new Map(recipients.map((recipient) => [recipient.id, recipient])),
    [recipients]
  );

  const update = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const chooseRecipient = (recipientId) => {
    if (recipientId === 'manual') {
      setForm((current) => ({ ...current, recipient_user_id: 'manual' }));
      return;
    }
    const recipient = recipientById.get(recipientId);
    if (!recipient) return;
    setForm((current) => ({
      ...current,
      recipient_user_id: recipient.id,
      recipient_name: recipient.name,
      recipient_email: recipient.email,
    }));
  };

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm(project));
    setDialogOpen(true);
  };

  const openEdit = (note) => {
    setEditing(note);
    setForm(noteForm(note));
    setDialogOpen(true);
  };

  const payload = () => ({
    project_id: project.id,
    ...form,
    recipient_user_id: form.recipient_user_id === 'manual' ? null : form.recipient_user_id,
    scheduled_start: apiDateTime(form.scheduled_start),
    scheduled_end: apiDateTime(form.scheduled_end),
  });

  const validate = (sendNow) => {
    if (!form.recipient_name.trim() || !form.recipient_email.trim()) {
      toast.error('Recipient name and email are required. / Name und E-Mail des Empfängers sind erforderlich.');
      return false;
    }
    if (sendNow && (!form.scheduled_start || !form.service_location.trim() || !form.job_scope.trim())) {
      toast.error('Start, job location and scope are required before sending. / Start, Einsatzort und Arbeitsumfang sind vor dem Versand erforderlich.');
      return false;
    }
    if (form.scheduled_start && form.scheduled_end && new Date(form.scheduled_end) <= new Date(form.scheduled_start)) {
      toast.error('End time must be after start time. / Das Ende muss nach dem Start liegen.');
      return false;
    }
    return true;
  };

  const save = async (sendNow = false) => {
    if (!validate(sendNow)) return;
    setSaving(true);
    try {
      const response = editing
        ? await api.put(`/service-notes/${editing.id}`, payload())
        : await api.post('/service-notes', payload());
      const saved = response.data;
      if (sendNow) {
        await api.post(`/service-notes/${saved.id}/send`, {});
        toast.success('Service Note emailed. / Einsatzauftrag wurde per E-Mail gesendet.');
      } else {
        toast.success('Service Note saved as draft. / Einsatzauftrag als Entwurf gespeichert.');
      }
      setDialogOpen(false);
      setEditing(null);
      await load();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Unable to save Service Note. / Einsatzauftrag konnte nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  };

  const sendExisting = async (note) => {
    setSendingId(note.id);
    try {
      await api.post(`/service-notes/${note.id}/send`, {});
      toast.success('Service Note emailed. / Einsatzauftrag wurde per E-Mail gesendet.');
      await load();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Email delivery failed. / E-Mail-Versand fehlgeschlagen.');
    } finally {
      setSendingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="font-semibold text-slate-900">Service Notes / Einsatzaufträge</h3>
          <p className="text-sm text-slate-500">Send complete project and site instructions to an employee or subcontractor.</p>
        </div>
        <Button className="bg-blue-600 hover:bg-blue-700" onClick={openNew} data-testid="add-service-note-button">
          <Plus className="mr-2 h-4 w-4" /> New Service Note
        </Button>
      </div>

      {notes.length === 0 ? (
        <Card className="border border-slate-200">
          <CardContent className="p-8 text-center text-slate-500">
            <ClipboardList className="mx-auto mb-3 h-10 w-10 text-slate-300" />
            No service notes yet. / Noch keine Einsatzaufträge.
          </CardContent>
        </Card>
      ) : (
        <Card className="border border-slate-200 shadow-sm">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Number / Nr.</TableHead>
                  <TableHead>Recipient / Empfänger</TableHead>
                  <TableHead>Type / Typ</TableHead>
                  <TableHead>Scheduled / Termin</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Response / Rückmeldung</TableHead>
                  <TableHead className="text-right">Actions / Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {notes.map((note) => (
                  <TableRow key={note.id}>
                    <TableCell className="font-medium">{note.service_note_number}</TableCell>
                    <TableCell>
                      <div>{note.recipient_name}</div>
                      <div className="text-xs text-slate-500">{note.recipient_email}</div>
                    </TableCell>
                    <TableCell className="capitalize">{note.recipient_type}</TableCell>
                    <TableCell>{formatSchedule(note.scheduled_start)}</TableCell>
                    <TableCell><Badge className={statusClass(note.status)}>{note.status}</Badge></TableCell>
                    <TableCell>
                      {note.recipient_type === 'subcontractor' ? (
                        <div className="space-y-1">
                          <Badge className={responseClass(note.response_status)}>{(note.response_status || 'not_required').replaceAll('_', ' ')}</Badge>
                          {note.response_status === 'alternative_proposed' && note.alternative_start && (
                            <div className="text-xs text-slate-600">Suggested: {formatSchedule(note.alternative_start)}</div>
                          )}
                          {note.response_comment && <div className="max-w-[240px] text-xs text-slate-500">{note.response_comment}</div>}
                        </div>
                      ) : '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => previewDocumentPdf('service-note', note.id, note.service_note_number)}
                          title="Preview PDF / PDF-Vorschau"
                          data-testid={`preview-service-note-${note.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        {note.status !== 'sent' && (
                          <Button size="sm" variant="ghost" onClick={() => openEdit(note)} title="Edit / Bearbeiten">
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={sendingId === note.id}
                          onClick={() => sendExisting(note)}
                          title={note.status === 'sent' ? 'Send again / Erneut senden' : 'Send email / E-Mail senden'}
                        >
                          <Mail className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Dialog open={dialogOpen} onOpenChange={(open) => !saving && setDialogOpen(open)}>
        <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Service Note / Einsatzauftrag bearbeiten' : 'New Service Note / Neuer Einsatzauftrag'}</DialogTitle>
          </DialogHeader>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Recipient type / Empfängertyp</Label>
              <Select value={form.recipient_type} onValueChange={(value) => update('recipient_type', value)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="employee">Employee / Mitarbeiter</SelectItem>
                  <SelectItem value="subcontractor">Subcontractor / Subunternehmer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Saved team member / Gespeicherter Mitarbeiter</Label>
              <Select value={form.recipient_user_id || 'manual'} onValueChange={chooseRecipient}>
                <SelectTrigger><SelectValue placeholder="Manual recipient" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manual recipient / Manuelle Eingabe</SelectItem>
                  {recipients.map((recipient) => (
                    <SelectItem key={recipient.id} value={recipient.id}>{recipient.name} — {recipient.role}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input value={form.recipient_name} onChange={(event) => update('recipient_name', event.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Email / E-Mail *</Label>
              <Input type="email" value={form.recipient_email} onChange={(event) => update('recipient_email', event.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Start *</Label>
              <Input type="datetime-local" value={form.scheduled_start} onChange={(event) => update('scheduled_start', event.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>End / Ende</Label>
              <Input type="datetime-local" value={form.scheduled_end} onChange={(event) => update('scheduled_end', event.target.value)} />
            </div>
          </div>

          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Customer / Kunde</Label>
              <Input value={form.customer_name} onChange={(event) => update('customer_name', event.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Site contact / Ansprechpartner</Label>
              <Input value={form.customer_contact_name} onChange={(event) => update('customer_contact_name', event.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Phone / Telefon</Label>
              <Input value={form.customer_phone} onChange={(event) => update('customer_phone', event.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Job location / Einsatzort *</Label>
              <Input value={form.service_location} onChange={(event) => update('service_location', event.target.value)} />
            </div>
          </div>

          <div className="mt-4 space-y-4">
            <div className="space-y-2">
              <Label>Scope of work / Arbeitsumfang *</Label>
              <Textarea rows={4} value={form.job_scope} onChange={(event) => update('job_scope', event.target.value)} placeholder="What must be completed, tested and documented?" />
            </div>
            <div className="space-y-2">
              <Label>Tools & materials / Werkzeuge & Material</Label>
              <Textarea rows={3} value={form.tools_materials} onChange={(event) => update('tools_materials', event.target.value)} placeholder="Required tools, parts, equipment, drawings, credentials…" />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Access instructions / Zugangshinweise</Label>
                <Textarea rows={3} value={form.access_instructions} onChange={(event) => update('access_instructions', event.target.value)} placeholder="Keys, access code, reception, site contact…" />
              </div>
              <div className="space-y-2">
                <Label>Parking & arrival / Parken & Anfahrt</Label>
                <Textarea rows={3} value={form.parking_instructions} onChange={(event) => update('parking_instructions', event.target.value)} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Safety notes / Sicherheitshinweise</Label>
              <Textarea rows={3} value={form.safety_notes} onChange={(event) => update('safety_notes', event.target.value)} placeholder="PPE, hazards, permits, lockout, working at height…" />
            </div>
            <div className="space-y-2 rounded-md border border-amber-200 bg-amber-50 p-3">
              <Label>Internal notes / Interne Notizen (not emailed / nicht per E-Mail versendet)</Label>
              <Textarea rows={2} value={form.internal_notes} onChange={(event) => update('internal_notes', event.target.value)} />
            </div>
          </div>

          <DialogFooter className="mt-5 gap-2">
            <Button variant="outline" disabled={saving} onClick={() => setDialogOpen(false)}>Cancel / Abbrechen</Button>
            <Button variant="outline" disabled={saving} onClick={() => save(false)}>Save draft / Entwurf speichern</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" disabled={saving} onClick={() => save(true)}>
              <Mail className="mr-2 h-4 w-4" /> Save & Email / Speichern & senden
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
